import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from './ui/dialog';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { Briefcase, Plus, ChevronRight, Search, Loader2 } from 'lucide-react';

const STATUS_COLORS = {
  Planned: 'bg-blue-100 text-blue-700', Active: 'bg-emerald-100 text-emerald-700',
  'On Hold': 'bg-amber-100 text-amber-700', Completed: 'bg-gray-100 text-gray-700', Archived: 'bg-gray-100 text-gray-500',
};

/**
 * Bidirectional Engagements panel for Contact / Organization / Opportunity detail.
 * Props:
 *   scope: 'contact' | 'organization' | 'opportunity'
 *   entityId, entityName
 *   canEdit (bool)
 */
export default function EngagementsSection({ scope, entityId, entityName, canEdit = true }) {
  const navigate = useNavigate();
  const { get, post } = useApi();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      setItems((await get(`/engagements/by-${scope}/${entityId}`)) || []);
    } catch (e) { /* noop */ } finally { setLoading(false); }
  }, [get, scope, entityId]);

  useEffect(() => { if (entityId) fetchItems(); }, [entityId, fetchItems]);

  const startEngagement = () => {
    const key = scope === 'organization' ? 'organization_id' : scope === 'opportunity' ? 'opportunity_id' : 'contact_id';
    const params = new URLSearchParams({ create: 'true', [key]: entityId });
    if (entityName) params.set('context_name', entityName);
    navigate(`/engagements?${params.toString()}`);
  };

  // "Add to Existing" — only for contacts (link this contact to an engagement).
  useEffect(() => {
    if (!addOpen) return;
    const t = setTimeout(async () => {
      try {
        const res = await get('/engagements', query ? { search: query } : undefined);
        const mine = new Set(items.map((i) => i.id));
        setResults((res || []).filter((e) => !mine.has(e.id)));
      } catch (e) { /* noop */ }
    }, 250);
    return () => clearTimeout(t);
  }, [addOpen, query, get, items]);

  const addToExisting = async (engagementId) => {
    try {
      await post(`/engagements/${engagementId}/contacts`, { contact_id: entityId });
      toast.success('Added to engagement');
      setAddOpen(false);
      fetchItems();
    } catch (err) { toast.error(err.message); }
  };

  return (
    <Card className="rounded-sm border" data-testid={`engagements-section-${scope}`}>
      <CardHeader className="pb-4 flex flex-row items-center justify-between">
        <CardTitle className="text-base font-medium">Engagements ({items.length})</CardTitle>
        {canEdit && (
          <div className="flex gap-2">
            {scope === 'contact' && (
              <Button size="sm" variant="ghost" className="rounded-sm" onClick={() => { setQuery(''); setResults([]); setAddOpen(true); }} data-testid="section-add-existing-engagement">
                Add to Existing
              </Button>
            )}
            <Button size="sm" variant="outline" className="rounded-sm" onClick={startEngagement} data-testid="section-start-engagement">
              <Plus className="h-4 w-4 mr-1.5" /> Start Engagement
            </Button>
          </div>
        )}
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : items.length === 0 ? (
          <div className="text-center py-8"><Briefcase className="h-10 w-10 mx-auto mb-3 text-muted-foreground" /><p className="text-muted-foreground">No engagements</p></div>
        ) : (
          <div className="space-y-2">
            {items.map((e) => (
              <button key={e.id} onClick={() => navigate(`/engagements/${e.id}`)} className="w-full flex items-center justify-between p-3 bg-muted/30 rounded-sm hover:bg-muted/50 transition-colors text-left" data-testid={`section-engagement-${e.id}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap"><p className="font-medium truncate">{e.name}</p><Badge className={`rounded-sm text-xs ${STATUS_COLORS[e.status] || ''}`}>{e.status}</Badge></div>
                  {e.type && <p className="text-xs text-muted-foreground mt-0.5">{e.type}</p>}
                </div>
                <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              </button>
            ))}
          </div>
        )}
      </CardContent>

      {scope === 'contact' && (
        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogContent className="sm:max-w-[520px]">
            <DialogHeader><DialogTitle>Add to Existing Engagement</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" /><Input autoFocus placeholder="Search engagements..." value={query} onChange={(e) => setQuery(e.target.value)} className="pl-9 rounded-sm" data-testid="section-engagement-search" /></div>
              <div className="max-h-[320px] overflow-y-auto space-y-1" data-testid="section-engagement-results">
                {results.length === 0 ? <p className="text-sm text-muted-foreground text-center py-6">No engagements found</p> : results.map((e) => (
                  <button key={e.id} onClick={() => addToExisting(e.id)} className="w-full flex items-center justify-between p-3 rounded-sm hover:bg-muted/50 text-left" data-testid={`section-engagement-option-${e.id}`}>
                    <span className="font-medium truncate">{e.name}</span>
                    <Badge className={`rounded-sm text-xs ${STATUS_COLORS[e.status] || ''}`}>{e.status}</Badge>
                  </button>
                ))}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </Card>
  );
}
